import { ApiException, ProblemDetails, ProspectCodeRequest } from '@shared/webapi/MIF.Subscription.WebApi';
import {
  ClientAuthErrorCode,
  ClientAuthRequest,
  RequestCodeClientAuthError,
  RequestCodeClientProblemDetails,
  ValidateCodeClientAuthError
} from './client.models';

export function isProspectCodeRequest(request: ClientAuthRequest): request is ProspectCodeRequest {
  return 'email' in request && 'phoneNumber' in request;
}

export function hasPartnerCodeValidationError(error: unknown): boolean {
  const problem = getApiProblemSource(error);
  const errors = problem?.errors;
  return problem?.status === 400 && isObject(errors) &&
    Array.isArray(errors['AdvertisementSource.PartnerCode']) &&
    errors['AdvertisementSource.PartnerCode'].length > 0;
}

export function parseRequestCodeClientError(error: unknown): RequestCodeClientAuthError {
  const problemDetails = getProblemDetails(error);
  const errorCode = getClientAuthErrorCode(problemDetails);

  return {
    detail: problemDetails?.detail ?? 'Une erreur est survenue.',
    errorCode,
    lockoutEnd: errorCode === ClientAuthErrorCode.AccessBlocked ? (problemDetails?.lockoutEnd ?? null) : null,
    remainingAttempts: null,
    status: problemDetails?.status ?? null,
    title: problemDetails?.title ?? null
  };
}

export function parseValidateCodeClientError(error: unknown): ValidateCodeClientAuthError {
  const problemDetails = getProblemDetails(error);
  const errorCode = getValidateCodeClientAuthErrorCode(problemDetails);

  return {
    detail: problemDetails?.detail ?? 'Une erreur est survenue.',
    errorCode,
    lockoutEnd: errorCode === ClientAuthErrorCode.AccessBlocked ? (problemDetails?.lockoutEnd ?? null) : null,
    remainingAttempts: isInvalidCodeClientAuthErrorCode(errorCode) ? (problemDetails?.remainingAttempts ?? null) : null,
    status: problemDetails?.status ?? null,
    title: problemDetails?.title ?? null
  };
}

export function isInvalidCodeClientAuthError(authError: ValidateCodeClientAuthError | null): boolean {
  return !!authError && isInvalidCodeClientAuthErrorCode(authError.errorCode);
}

function getProblemDetails(error: unknown): RequestCodeClientProblemDetails | null {
  const apiError = getApiProblemSource(error);

  if (!apiError) {
    return null;
  }

  return ProblemDetails.fromJS(apiError) as RequestCodeClientProblemDetails;
}

function getApiProblemSource(error: unknown): Record<string, unknown> | null {
  if (ApiException.isApiException(error) && error.response) {
    return tryParseProblemSource(error.response);
  }

  if (isObject(error) && typeof error.error === 'string') {
    const parsedError = tryParseProblemSource(error.error);

    if (parsedError) {
      return parsedError;
    }
  }

  if (isObject(error) && isObject(error.error)) {
    return error.error;
  }

  if (typeof error === 'string') {
    return tryParseProblemSource(error);
  }

  if (isObject(error)) {
    return error;
  }

  return null;
}

function tryParseProblemSource(value: string): Record<string, unknown> | null {
  try {
    const parsedValue = JSON.parse(value) as unknown;

    return isObject(parsedValue) ? parsedValue : null;
  } catch {
    return null;
  }
}

function getClientAuthErrorCode(problemDetails: RequestCodeClientProblemDetails | null): ClientAuthErrorCode | null {
  const errorCode = problemDetails?.errorCode;

  if (errorCode === ClientAuthErrorCode.InvalidCredentials) {
    return ClientAuthErrorCode.InvalidCredentials;
  }

  if (isInvalidCodeClientAuthProblemCode(errorCode)) {
    return ClientAuthErrorCode.InvalidCodeOrSessionId;
  }

  if (errorCode === ClientAuthErrorCode.AccessBlocked) {
    return ClientAuthErrorCode.AccessBlocked;
  }

  if (errorCode === ClientAuthErrorCode.CodeDeliveryFailed) {
    return ClientAuthErrorCode.CodeDeliveryFailed;
  }

  if (errorCode === ClientAuthErrorCode.TooManyRequests) {
    return ClientAuthErrorCode.TooManyRequests;
  }

  if (problemDetails?.status === 429) {
    return ClientAuthErrorCode.TooManyRequests;
  }

  return null;
}

function getValidateCodeClientAuthErrorCode(
  problemDetails: RequestCodeClientProblemDetails | null
): ClientAuthErrorCode | null {
  const errorCode = getClientAuthErrorCode(problemDetails);

  if (errorCode) {
    return errorCode;
  }

  if (problemDetails?.status === 401) {
    return ClientAuthErrorCode.InvalidCodeOrSessionId;
  }

  return null;
}

function isInvalidCodeClientAuthErrorCode(errorCode: ClientAuthErrorCode | null): boolean {
  return errorCode === ClientAuthErrorCode.InvalidCodeOrSessionId;
}

function isInvalidCodeClientAuthProblemCode(errorCode: string | null | undefined): boolean {
  return errorCode === ClientAuthErrorCode.InvalidCodeOrSessionId || errorCode === 'InvalidCodeOrSesionId';
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
